import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";

import { orpc } from "@/client";
import { Modal } from "@/components/ui/modal";
import { SkeletonRows } from "@/components/ui/skeleton";
import { MedicoLabels } from "@/lib/labels/medico";
import { cn } from "@/lib/utils";
import { djs } from "@/lib/djs";

interface Sentence {
	index: number;
	speaker: string | null;
	text: string;
}

function groupTurns(sentences: Sentence[]) {
	const turns: { speaker: string | null; sentences: Sentence[] }[] = [];

	for (const sentence of sentences) {
		const last = turns.at(-1);

		if (last !== undefined && last.speaker === sentence.speaker) {
			last.sentences.push(sentence);
			continue;
		}

		turns.push({ speaker: sentence.speaker, sentences: [sentence] });
	}

	return turns;
}

export function TranscriptModal({
	transcriptId,
	highlights = [],
	onClose,
}: {
	transcriptId: string;
	highlights?: number[];
	onClose: () => void;
}) {
	const { data, isPending, isError } = useQuery(
		orpc.medico.transcript.queryOptions({ input: { transcriptId } }),
	);

	const highlightRef = useRef<HTMLParagraphElement | null>(null);

	useEffect(() => {
		highlightRef.current?.scrollIntoView({ block: "center" });
	}, [data]);

	const highlighted = new Set(highlights);
	const firstHighlight = highlights.length > 0 ? Math.min(...highlights) : null;

	const turns = data === undefined ? [] : groupTurns(data.sentences);
	const speakers = new Set(turns.map((turn) => turn.speaker));
	speakers.delete(null);
	const [primary = null] = speakers;
	const dialogue = speakers.size >= 2;

	const sentenceClass = (index: number) =>
		cn(
			"whitespace-pre-wrap break-words",
			highlighted.has(index) && "-mx-1.5 rounded-md bg-warning-soft px-1.5 py-0.5",
		);

	return (
		<Modal
			component="transcript-modal"
			className="max-w-2xl"
			title={data?.title ?? MedicoLabels.transcriptFallbackTitle}
			onClose={onClose}
			eyebrow={
				data === undefined ? undefined : (
					<>
						<span>
							{highlights.length > 0 && MedicoLabels.evidenceCount(highlights.length)}
							{highlights.length === 0 && MedicoLabels.origin[data.origin]}
						</span>

						<span className="font-mono">{djs(data.recordedAt).format("DD/MM HH:mm")}</span>
					</>
				)
			}
		>
			{isPending && <SkeletonRows rows={6} />}

			{isError && (
				<p className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{MedicoLabels.transcriptLoadError}
				</p>
			)}

			{!dialogue && (
				<div className="flex flex-col gap-2">
					{turns.flatMap((turn) =>
						turn.sentences.map((sentence) => (
							<p
								key={sentence.index}
								ref={sentence.index === firstHighlight ? highlightRef : null}
								className={cn(
									"text-sm leading-relaxed text-foreground",
									sentenceClass(sentence.index),
								)}
							>
								{sentence.text}
							</p>
						)),
					)}
				</div>
			)}

			{dialogue && (
				<ol className="flex flex-col gap-3">
					{turns.map((turn) => {
						const fromPrimary = turn.speaker === primary;

						return (
							<li
								key={turn.sentences[0]?.index}
								className={cn("flex flex-col", fromPrimary ? "items-end" : "items-start")}
							>
								<div
									className={cn(
										"flex max-w-[85%] flex-col gap-1 rounded-lg px-3.5 py-2 text-sm leading-relaxed text-foreground",
										fromPrimary
											? "rounded-br-sm bg-primary-soft"
											: "rounded-bl-sm border border-border bg-card",
									)}
								>
									{!fromPrimary && speakers.size > 2 && turn.speaker !== null && (
										<span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
											{MedicoLabels.transcriptVoice(turn.speaker)}
										</span>
									)}

									{turn.sentences.map((sentence) => (
										<p
											key={sentence.index}
											ref={sentence.index === firstHighlight ? highlightRef : null}
											className={sentenceClass(sentence.index)}
										>
											{sentence.text}
										</p>
									))}
								</div>
							</li>
						);
					})}
				</ol>
			)}
		</Modal>
	);
}
